/**
 * ContactDetail — 2026-10 (kompakte Version)
 * ------------------------------------------------------------------
 * Übersichtsseite eines Kontakts mit allen Verknüpfungen, Ansprechpartnern
 * und eigener Dokumentenmappe.
 *
 * - Nur befüllte Sektionen werden angezeigt (keine leeren Placeholder).
 * - Kennzahlen im Header inline (statt großer Tiles).
 * - Ansprechpartner (bei Firmen) mit Direkt-Message-Button.
 */
import CommunicationTab from "@/components/communication/CommunicationTab";
import { ChatsCircle } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SignedScanUploadButton } from "@/components/messaging/SignedScanUploadButton";
import api, { API, getToken } from "@/lib/api";
import { CONTACT_ROLES, contactDisplayName, firstEmail, firstPhone, formatDate, formatEUR } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog } from "@/components/ui/dialog";
import { ArrowLeft, PaperPlaneTilt, PencilSimple, House, DoorOpen, FileText, Users, Bank, Ticket, Buildings, Folder, Download, Eye, Envelope, Phone, MapPin, UserCircle, Star, Plus, Trash, StarFour } from "@phosphor-icons/react";
import MessagingDialog from "@/components/MessagingDialog";
import { ContactDialog } from "@/pages/Contacts";
import { ContactPhoto } from "@/components/contacts/ContactPhoto";

const CONTACT_TYPE_LABELS = {
  person: "Person", company: "Firma", institution: "Institution",
  court: "Gericht", authority_office: "Behörde", weg_community: "WEG-Eigentümergemeinschaft",
};

const PROP_ROLE_LABELS = {
  owner: "Eigentümer", co_owner: "Mit-Eigentümer", caretaker: "Hausmeister",
  beirat: "Verwaltungsbeirat", weg_manager: "Fremd-Verwalter",
};

const DOC_SOURCE_LABELS = {
  direkt: "direkt",
  gesendet_an: "gesendet",
  mietverhaeltnis: "aus MV",
  objekt: "aus Objekt",
};

export default function ContactDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [msgTarget, setMsgTarget] = useState(null); // {contact_id, email, preselected}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/contacts/${id}/overview`);
      setData(r.data);
    } catch (e) {
      toast.error("Kontakt konnte nicht geladen werden");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !data) {
    return <div className="p-6 text-sm text-slate-500">Lädt…</div>;
  }
  const c = data.contact;
  const counts = data.counts;

  // Öffnet Message-Dialog. Für Ansprechpartner: Firma als Basiskontext +
  // preselected `<firmId>:cp:<apId>` (bzw. `:cp:__legacy__`), damit die
  // Firmenanschrift verwendet wird und die AP-Mail als Default gewählt ist.
  const openMsg = (targetContactId, email) => setMsgTarget({
    contact_id: targetContactId,
    email: email || "",
    preselected: null,
  });
  const openMsgForAp = (ap) => setMsgTarget({
    contact_id: c.id, // Firma bleibt Basiskontext
    email: ap.email || "",
    preselected: [`${c.id}:cp:${ap.id || "__legacy__"}`],
  });

  // 2026-10 · Dokument öffnen (inline im neuen Tab). Nutzt Auth-Header + Blob-URL.
  const openDoc = async (d, mode = "view") => {
    try {
      const r = await fetch(`${API}/documents/${d.id}/download`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      if (!r.ok) throw new Error("Öffnen fehlgeschlagen");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      if (mode === "download") {
        const a = document.createElement("a");
        a.href = url;
        a.download = d.name || "dokument.pdf";
        a.click();
      } else {
        window.open(url, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-6xl" data-testid="contact-detail-page">
      {/* Zurück */}
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
        data-testid="contact-detail-back"
      >
        <ArrowLeft size={12} /> Zurück
      </button>

      {/* HEADER — kompakt */}
      <div className="bg-white border border-slate-200 rounded-sm">
        <div className="px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <ContactPhoto contact={c} />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.14em] font-semibold text-slate-500 mb-0.5">
              {CONTACT_TYPE_LABELS[c.contact_type] || c.contact_type}
              {c.owner_number && <span className="ml-2 font-mono text-slate-700">· {c.owner_number}</span>}
            </div>
            <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 tracking-tight" data-testid="contact-detail-name">
              {contactDisplayName(c)}
            </h1>
            {(c.roles || []).length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {(c.roles || []).map((r) => (
                  <Badge key={r} variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200">
                    {CONTACT_ROLES[r] || r}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              variant="outline"
              size="sm"
              className="gap-2 h-8"
              onClick={() => openMsg(c.id, firstEmail(c.emails))}
              data-testid="contact-detail-write"
            >
              <PaperPlaneTilt size={14} /> Nachricht
            </Button>
            <Button
              size="sm"
              className="gap-2 h-8 bg-slate-900 hover:bg-slate-800 text-white"
              onClick={() => setEditOpen(true)}
              data-testid="contact-detail-edit"
            >
              <PencilSimple size={14} /> Bearbeiten
            </Button>
          </div>
        </div>

        {/* Stammdaten-Zeile + Kennzahlen inline */}
        <div className="border-t border-slate-100 px-5 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-xs">
          <MetaItem icon={<Envelope size={12} />} value={firstEmail(c.emails)} empty="—" />
          <MetaItem icon={<Phone size={12} />} value={firstPhone(c.phones)} empty="—" mono />
          <MetaItem
            icon={<MapPin size={12} />}
            value={c.address?.street ? `${c.address.street}, ${c.address.zip || ""} ${c.address.city || ""}`.trim() : null}
            empty="—"
          />
          <div className="flex-1 min-w-0" />
          {/* Zähler-Pills */}
          <CountPill n={counts.ansprechpartner} label="AP" show={counts.ansprechpartner > 0} />
          <CountPill n={counts.properties} label="Objekte" show={counts.properties > 0} />
          <CountPill n={counts.units} label="Einheiten" show={counts.units > 0} />
          <CountPill n={counts.tenancies} label="MV" show={counts.tenancies > 0} />
          <CountPill n={counts.weg_memberships} label="WEG" show={counts.weg_memberships > 0} />
          <CountPill n={counts.bank_accounts} label="Bank" show={counts.bank_accounts > 0} />
          <CountPill n={counts.tickets} label="Tickets" show={counts.tickets > 0} />
          <CountPill n={counts.vorgaenge} label="Vorgänge" show={counts.vorgaenge > 0} />
          <CountPill n={counts.documents} label="Dokumente" show={counts.documents > 0} />
        </div>
      </div>

      {/* ANSPRECHPARTNER (bei Firmen) */}
      {data.ansprechpartner && data.ansprechpartner.length > 0 && (
        <Section title="Ansprechpartner" icon={<UserCircle size={14} />} count={data.ansprechpartner.length}>
          <ul className="divide-y divide-slate-100">
            {data.ansprechpartner.map((ap) => (
              <li key={ap.id || ap.display_name} className="py-2 flex items-center justify-between gap-3" data-testid={`contact-ap-${ap.id || "legacy"}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    {ap.id ? (
                      <Link to={`/kontakte/${ap.id}`} className="text-sm font-medium text-slate-900 hover:text-teal-700 hover:underline underline-offset-2">
                        {ap.display_name}
                      </Link>
                    ) : (
                      <span className="text-sm font-medium text-slate-900">{ap.display_name}</span>
                    )}
                    {ap.is_primary && <Star size={11} weight="fill" className="text-amber-500" title="Primärer Ansprechpartner" />}
                    {ap.function && <span className="text-[11px] text-slate-500">· {ap.function}</span>}
                  </div>
                  {(ap.email || ap.phone) && (
                    <div className="text-[11px] text-slate-500 truncate">
                      {[ap.email, ap.phone].filter(Boolean).join(" · ")}
                    </div>
                  )}
                </div>
                {!ap.legacy_text_only && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 hover:text-teal-700"
                    onClick={() => openMsgForAp(ap)}
                    title="Nachricht an Ansprechpartner"
                    data-testid={`contact-ap-write-${ap.id}`}
                  >
                    <PaperPlaneTilt size={14} />
                  </Button>
                )}
                {ap.legacy_text_only && (
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 hover:text-teal-700"
                    onClick={() => openMsgForAp(ap)}
                    title="Nachricht an Firma z. Hd. Ansprechpartner"
                    data-testid="contact-ap-write-legacy"
                  >
                    <PaperPlaneTilt size={14} />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Verknüpfungen — Grid, nur befüllte anzeigen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {data.properties.length > 0 && (
          <Section title="Objekte" icon={<House size={14} />} count={data.properties.length}>
            <ul className="divide-y divide-slate-100">
              {data.properties.map((p) => (
                <li key={p.id} className="py-1.5 flex items-center justify-between gap-3" data-testid={`contact-property-${p.id}`}>
                  <div className="min-w-0 flex-1">
                    <Link to={`/objekte/${p.id}`} className="text-sm font-medium text-slate-900 hover:text-teal-700 hover:underline underline-offset-2 truncate block">
                      {p.display_name}
                    </Link>
                    {(p.street || p.city) && (
                      <div className="text-[11px] text-slate-500 truncate">
                        {[p.street, [p.zip, p.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1 justify-end shrink-0">
                    {p.roles.map((r) => (
                      <Badge key={r} variant="secondary" className="text-[10px] bg-teal-50 text-teal-800 border border-teal-200">
                        {PROP_ROLE_LABELS[r] || r}
                      </Badge>
                    ))}
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {/* iter168 · Multi-Adress-Verwaltung */}
        <AddressesSection contact={c} onUpdated={load} />

        {data.units.length > 0 && (
          <Section title="Einheiten" icon={<DoorOpen size={14} />} count={data.units.length}>
            <ul className="divide-y divide-slate-100">
              {data.units.map((u) => (
                <li key={u.id} className="py-1.5 flex items-center justify-between gap-3" data-testid={`contact-unit-${u.id}`}>
                  <Link to={`/objekte/${u.property_id}/einheiten/${u.id}`} className="text-sm font-medium text-slate-900 hover:text-teal-700 hover:underline underline-offset-2 truncate">
                    {u.name}
                  </Link>
                  {u.share_percent != null && (
                    <div className="text-xs text-slate-600 tabular-nums shrink-0">
                      {u.share_percent.toFixed(2).replace(".", ",")} %
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {data.tenancies.length > 0 && (
          <Section title="Mietverhältnisse" icon={<Users size={14} />} count={data.tenancies.length}>
            <ul className="divide-y divide-slate-100">
              {data.tenancies.map((t) => (
                <li key={t.id} className="py-1.5 flex items-center justify-between gap-3" data-testid={`contact-tenancy-${t.id}`}>
                  <div className="min-w-0 flex-1">
                    <Link to={`/mietverhaeltnisse/${t.id}`} className="text-sm font-medium text-slate-900 hover:text-teal-700 hover:underline underline-offset-2 truncate block">
                      {t.contract_number || t.id.slice(0, 8)}
                    </Link>
                    <div className="text-[11px] text-slate-500">
                      {t.contract_start && `ab ${formatDate(t.contract_start)}`}
                      {t.contract_end && ` – ${formatDate(t.contract_end)}`}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {t.rent_cold != null && (
                      <span className="text-xs text-slate-600 tabular-nums">{formatEUR(t.rent_cold)}</span>
                    )}
                    <Badge variant="secondary" className={`text-[10px] ${t.status === "aktiv" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-600 border-slate-200"} border`}>
                      {t.status}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {data.bank_accounts.length > 0 && (
          <Section title="Bankkonten" icon={<Bank size={14} />} count={data.bank_accounts.length}>
            <ul className="divide-y divide-slate-100">
              {data.bank_accounts.map((b, i) => (
                <li key={b.id || `${b.iban}-${i}`} className="py-1.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-mono text-slate-900 truncate">{b.iban || "—"}</div>
                    {(b.bank_name || b.bic) && (
                      <div className="text-[11px] text-slate-500 truncate">{[b.bank_name, b.bic].filter(Boolean).join(" · ")}</div>
                    )}
                  </div>
                  {b.is_default && (
                    <Badge variant="secondary" className="text-[10px] bg-teal-50 text-teal-800 border border-teal-200 shrink-0">Standard</Badge>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}

        {data.weg_memberships.length > 0 && (
          <Section title="WEG-Mitglieder (Einheiten)" icon={<Buildings size={14} />} count={data.weg_memberships.length}>
            <ul className="divide-y divide-slate-100">
              {data.weg_memberships.map((u) => (
                <li key={u.unit_id} className="py-1.5">
                  <Link to={`/objekte/${u.property_id}/einheiten/${u.unit_id}`} className="text-sm font-medium text-slate-900 hover:text-teal-700 hover:underline underline-offset-2">
                    {u.name}
                  </Link>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {data.tickets.length > 0 && (
          <Section title="Tickets" icon={<Ticket size={14} />} count={data.tickets.length}>
            <ul className="divide-y divide-slate-100">
              {data.tickets.slice(0, 20).map((t) => (
                <li key={t.id} className="py-1.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{t.title}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{t.ticket_number}</div>
                  </div>
                  <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                    {t.status}
                  </Badge>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {data.vorgaenge.length > 0 && (
          <Section title="Vorgänge" icon={<FileText size={14} />} count={data.vorgaenge.length}>
            <ul className="divide-y divide-slate-100">
              {data.vorgaenge.slice(0, 20).map((v) => (
                <li key={v.id} className="py-1.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{v.title || v.case_number || v.id.slice(0, 8)}</div>
                    {v.case_number && <div className="text-[11px] text-slate-500 font-mono">{v.case_number}</div>}
                  </div>
                  {v.status && (
                    <Badge variant="secondary" className="text-[10px] bg-slate-100 text-slate-700 border border-slate-200 shrink-0">
                      {v.status}
                    </Badge>
                  )}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>

      {/* DOKUMENTENMAPPE (immer sichtbar) */}
      <Section title="Kommunikation" icon={<ChatsCircle size={14} />} count={null}>
        <div className="p-3"><CommunicationTab scope={{ contact_id: c.id }} title={c.display_name || c.company_name || ""} /></div>
      </Section>

      <Section title="Dokumentenmappe" icon={<Folder size={14} />} count={data.documents.length}>
        {data.documents.length === 0 ? (
          <Empty text="Noch keine Dokumente" />
        ) : (
          <div className="overflow-x-auto -mx-3 sm:-mx-4">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-100">
                <tr>
                  <th className="px-3 py-2 font-semibold">Name</th>
                  <th className="px-3 py-2 font-semibold w-32">Kategorie</th>
                  <th className="px-3 py-2 font-semibold w-24">Herkunft</th>
                  <th className="px-3 py-2 font-semibold w-24">Datum</th>
                  <th className="px-3 py-2 text-right w-10"></th>
                </tr>
              </thead>
              <tbody>
                {data.documents.map((d) => (
                  <tr key={d.id} className="border-t border-slate-50 hover:bg-slate-50" data-testid={`contact-document-${d.id}`}>
                    <td className="px-3 py-1.5 max-w-md">
                      <button
                        type="button"
                        onClick={() => openDoc(d, "view")}
                        className="font-medium text-slate-900 hover:text-teal-700 hover:underline underline-offset-2 text-left truncate max-w-full"
                        title="Dokument anzeigen"
                        data-testid={`contact-document-view-${d.id}`}
                      >
                        {d.name || "—"}
                      </button>
                    </td>
                    <td className="px-3 py-1.5 text-slate-600 text-xs">{d.category || "—"}</td>
                    <td className="px-3 py-1.5">
                      <Badge variant="secondary" className={`text-[10px] border ${
                        d.source === "direkt" ? "bg-teal-50 text-teal-800 border-teal-200" :
                        d.source === "gesendet_an" ? "bg-rose-50 text-rose-800 border-rose-200" :
                        d.source === "mietverhaeltnis" ? "bg-indigo-50 text-indigo-800 border-indigo-200" :
                        "bg-amber-50 text-amber-800 border-amber-200"
                      }`}>
                        {DOC_SOURCE_LABELS[d.source] || d.source}
                      </Badge>
                    </td>
                    <td className="px-3 py-1.5 text-slate-600 tabular-nums text-xs">{d.created_at ? formatDate(d.created_at) : "—"}</td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        {d.signature_status === "pending_scan" && (
                          <SignedScanUploadButton documentId={d.id} onDone={load} testId={`contact-doc-scan-upload-${d.id}`} />
                        )}
                        <button
                          onClick={() => openDoc(d, "view")}
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md text-slate-500 hover:text-teal-700 hover:bg-slate-100"
                          title="Anzeigen"
                          data-testid={`contact-document-view-icon-${d.id}`}
                        >
                          <Eye size={12} />
                        </button>
                        <button
                          onClick={() => openDoc(d, "download")}
                          className="inline-flex items-center justify-center h-6 w-6 rounded-md text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                          title="Herunterladen"
                          data-testid={`contact-document-download-${d.id}`}
                        >
                          <Download size={12} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* Dialogs */}
      <Dialog open={editOpen} onOpenChange={(o) => { setEditOpen(o); if (!o) load(); }}>
        <ContactDialog editing={c} onSaved={() => { setEditOpen(false); load(); }} />
      </Dialog>
      <MessagingDialog
        open={!!msgTarget}
        onOpenChange={(o) => { if (!o) setMsgTarget(null); }}
        context={msgTarget ? {
          contact_id: msgTarget.contact_id,
          preselected_recipient_keys: msgTarget.preselected || undefined,
        } : {}}
        defaultEmail={msgTarget?.email || ""}
        defaultSubject=""
      />
    </div>
  );
}

function Section({ title, icon, count, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="px-3 sm:px-4 py-2 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-700">
          <span className="text-slate-500">{icon}</span>
          {title}
        </div>
        <span className="text-[10px] text-slate-500 tabular-nums font-semibold">{count}</span>
      </div>
      <div className="px-3 sm:px-4 py-2">{children}</div>
    </div>
  );
}

// iter168 · Multi-Adress-Support: Verwaltung mehrerer Anschriften
function AddressesSection({ contact, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const [editIdx, setEditIdx] = useState(null);
  const [form, setForm] = useState({ label: "", street: "", house_number: "", zip: "", city: "" });
  const addrs = contact.addresses || [];

  const save = async (next) => {
    setBusy(true);
    try {
      await api.patch(`/contacts/${contact.id}`, { ...contact, addresses: next });
      toast.success("Anschriften aktualisiert");
      onUpdated?.();
      setEditIdx(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler beim Speichern"); }
    finally { setBusy(false); }
  };

  const startAdd = () => {
    setEditIdx(-1);
    setForm({ label: "Zweitanschrift", street: "", house_number: "", zip: "", city: "" });
  };
  const startEdit = (i) => {
    const a = addrs[i];
    setEditIdx(i);
    setForm({
      label: a.label || "", street: a.street || "",
      house_number: a.house_number || "", zip: a.zip || "", city: a.city || "",
    });
  };
  const commit = () => {
    if (!form.street.trim() || !form.city.trim()) {
      toast.error("Straße und Ort sind erforderlich");
      return;
    }
    const next = [...addrs];
    if (editIdx === -1) {
      next.push({
        id: crypto.randomUUID(), label: form.label || "Anschrift",
        street: form.street.trim(), house_number: form.house_number.trim(),
        zip: form.zip.trim(), city: form.city.trim(),
        country: "Deutschland",
        is_primary: next.length === 0,
      });
    } else {
      next[editIdx] = {
        ...next[editIdx],
        label: form.label || "Anschrift",
        street: form.street.trim(), house_number: form.house_number.trim(),
        zip: form.zip.trim(), city: form.city.trim(),
      };
    }
    save(next);
  };
  const setPrimary = (i) => {
    const next = addrs.map((a, idx) => ({ ...a, is_primary: idx === i }));
    save(next);
  };
  const remove = (i) => {
    const next = addrs.filter((_, idx) => idx !== i);
    if (!next.some((a) => a.is_primary) && next.length > 0) next[0].is_primary = true;
    save(next);
  };

  return (
    <Section title="Anschriften" icon={<MapPin size={14} />} count={addrs.length}>
      {addrs.length === 0 && editIdx === null && (
        <div className="text-sm text-slate-500 py-2">Keine Anschriften hinterlegt.</div>
      )}
      <ul className="divide-y divide-slate-100" data-testid="addresses-list">
        {addrs.map((a, i) => (
          <li key={a.id || i} className="py-2 flex items-center gap-2" data-testid={`address-item-${i}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                {a.is_primary && <Star size={12} weight="fill" className="text-amber-500" title="Standard-Anschrift" />}
                <span className="text-sm font-medium">{a.label || "Anschrift"}</span>
              </div>
              <div className="text-xs text-slate-600">
                {a.street}{a.house_number ? ` ${a.house_number}` : ""}, {a.zip} {a.city}
              </div>
            </div>
            {!a.is_primary && (
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs"
                      onClick={() => setPrimary(i)} disabled={busy}
                      data-testid={`address-set-primary-${i}`}>
                <StarFour size={12} className="mr-1" /> Standard
              </Button>
            )}
            <Button size="icon" variant="ghost" className="h-7 w-7"
                    onClick={() => startEdit(i)} disabled={busy}
                    data-testid={`address-edit-${i}`}>
              <PencilSimple size={12} />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-600 hover:text-rose-700"
                    onClick={() => remove(i)} disabled={busy}
                    data-testid={`address-delete-${i}`}>
              <Trash size={12} />
            </Button>
          </li>
        ))}
      </ul>
      {editIdx !== null && (
        <div className="mt-3 border border-slate-200 rounded-md p-3 space-y-2 bg-slate-50" data-testid="address-form">
          <div className="grid grid-cols-3 gap-2">
            <input className="col-span-3 h-8 border rounded px-2 text-sm"
                   placeholder="Label (z. B. Hauptadresse, Rechnung)"
                   value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                   data-testid="address-form-label" />
            <input className="col-span-2 h-8 border rounded px-2 text-sm"
                   placeholder="Straße"
                   value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })}
                   data-testid="address-form-street" />
            <input className="h-8 border rounded px-2 text-sm"
                   placeholder="Nr."
                   value={form.house_number} onChange={(e) => setForm({ ...form, house_number: e.target.value })}
                   data-testid="address-form-hn" />
            <input className="h-8 border rounded px-2 text-sm"
                   placeholder="PLZ" maxLength={5}
                   value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })}
                   data-testid="address-form-zip" />
            <input className="col-span-2 h-8 border rounded px-2 text-sm"
                   placeholder="Ort"
                   value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })}
                   data-testid="address-form-city" />
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditIdx(null)}>Abbrechen</Button>
            <Button size="sm" className="bg-teal-600 hover:bg-teal-700"
                    onClick={commit} disabled={busy} data-testid="address-form-save">
              {editIdx === -1 ? "Hinzufügen" : "Speichern"}
            </Button>
          </div>
        </div>
      )}
      {editIdx === null && (
        <div className="pt-2">
          <Button size="sm" variant="outline" onClick={startAdd} disabled={busy}
                  data-testid="address-add">
            <Plus size={12} className="mr-1" /> Anschrift hinzufügen
          </Button>
        </div>
      )}
    </Section>
  );
}


function MetaItem({ icon, value, empty = "—", mono = false }) {
  return (
    <div className="flex items-center gap-1.5 min-w-0">
      <span className="text-slate-400 shrink-0">{icon}</span>
      <span className={`truncate ${value ? "text-slate-700" : "text-slate-400 italic"} ${mono ? "tabular-nums" : ""}`}>
        {value || empty}
      </span>
    </div>
  );
}

function CountPill({ n, label, show = true }) {
  if (!show) return null;
  return (
    <div className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-700">
      <span className="font-semibold tabular-nums">{n}</span>
      <span className="text-slate-500">{label}</span>
    </div>
  );
}

function Empty({ text }) {
  return <div className="text-xs text-slate-400 italic py-2 text-center">{text}</div>;
}
